import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { FeatureGroup, GeoJSON, useMapEvents } from "react-leaflet";
import type { GeoJsonObject } from "geojson";
import * as L from "leaflet";

import {
  Footer,
  MapActionBar,
  MapComponent,
  Navbar,
  ReportModal,
} from "../../Components";
import { DimensionControl } from "../../Components/DimensionControl";
import { RasterRangeControl } from "../../Components/RasterRangeControl";
import { HomeContainer } from "../styles";
import TabComponent from "../../Components/TabComponent";
import { ProximityHoverManager } from "../../Components/ProximityHoverManager";
import ToolsHandler from "../../Components/tools/ToolsHandler";
import { useLayers } from "../../context/LayersContext";
import { ToolsMenu } from "../../Components/tools/ToolsMenu";
import { InstructionCard } from "../../Components/InstructionCard";
import RenderImportedLayers from "../../Components/RenderImportedLayers";
import { RenderGeoServerLayers } from "../../Components/RenderGeoServerLayers";
import { RasterGradientLegend } from "../../Components/RasterGradientLegend";
import { ConjuntoDGCModal } from "../../Components/ConjuntoDGCModal";
import {
  StatisticsPanel,
  StatisticsPanelRef,
  StatisticsPanelTab,
} from "../../Components/Statistics/StatisticsPanel";
import { HomeJoyride } from "../../Components/Onboarding/HomeJoyride";
import { useRulerModalContext, MAP_TOOLS } from "../../context/RulerContext";
import { GeocodingSearchBar } from "../../Components/GeocodingSearchBar";
import { normalizeLatLng, isInsidePernambuco } from "../../utils/geo";
import {
  Geometry,
  ApiReverseGeocoding,
  RestrictionEntry,
} from "../../services/api";
import { toast } from "sonner";
import { Alert } from "../../Components/ui/shadcn/alert";
import {
  geoServer,
  geoServerGetCityCoordinatesParams,
} from "../../services/geoServer";
import { useSelectedArea, SystemMode } from "./useSelectedArea";
import { FerroviaSvgDefs } from "../../Components/FerroviaSvgDefs";
import { mockLayers } from "../../global/constants/layers";
import {
  PROJECTION_LAYER_OPTIONS,
  EOLICO_PROJECTION_LAYER_OPTIONS,
} from "../../global/constants/projectionLayers";
import {
  exportSelectedArea,
  isAreaExportable,
  AREA_EXPORT_MAX_AREA_HA,
} from "../../utils/areaExport";
import { computeAreaKm2 } from "../../utils/areaFormatting";

type PanelSection = "modal" | "tools" | "report" | "legend";

function MapClickHandler({
  onMapClick,
}: {
  onMapClick: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click: (e) => {
      // Clicks on buttons, links, inputs, or map UI controls inside the Leaflet
      // container bubble as map clicks — skip them so that opening the action bar,
      // report modal, or other controls does not trigger city selection changes.
      const target = e.originalEvent?.target as HTMLElement | null;
      if (target?.closest('button, a, input, select, [role="button"]')) return;
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// A página da Neoenergia não abre o acordeão via URL; o text fragment rola até
// o título e o destaca, e a âncora do Liferay é o fallback para navegadores sem
// suporte a text fragments.
const NEOENERGIA_VIABILIDADE_URL =
  "https://www.neoenergia.com/web/pernambuco/seu-negocio/nossos-servicos" +
  "#fragment-d0c887c9-ef01-a3be-384a-1f6c2ba7e3ed" +
  ":~:text=Disponibilidade%20de%20Energia%20(Viabilidade)";

// Micro e minigeração (até 3 MW em fontes não despacháveis, como solar) segue o
// processo de acesso de Geração Distribuída, não a Disponibilidade de Energia.
const NEOENERGIA_GERACAO_DISTRIBUIDA_URL =
  "https://www.neoenergia.com/web/pernambuco/seu-negocio/geracao-distribuida" +
  "#:~:text=Como%20solicitar%20acesso%20%C3%A0%20Gera%C3%A7%C3%A3o%20Distribu%C3%ADda";

// Usinas acima de minigeração seguem as normas de centrais geradoras
// (DIS-NOR-067 para 69/88/138 kV, DIS-NOR-033 para média tensão); não há página
// de serviço para elas, então o link cai na seção dessas normas.
const NEOENERGIA_CENTRAIS_GERADORAS_URL =
  "https://www.neoenergia.com/web/pernambuco/normas-tecnicas" +
  "#:~:text=Conex%C3%B5es%20de%20Geradores%20ao%20Sistema%20de%20Distribui%C3%A7%C3%A3o";

export function Home() {
  const {
    layers,
    layerCqlFilters,
    setOnSocioambientalLayerAdded,
    setOnAplLayerAdded,
    setOnSgbLayerAdded,
    setOnDgcLayerAdded,
    setOnDistributionSubstationLayerAdded,
  } = useLayers();

  const featureGroupRef = useRef<L.FeatureGroup>(null);
  const statisticsPanelRef = useRef<StatisticsPanelRef>(null);
  const { activeFunction, drawings, activeFunctionHandler } =
    useRulerModalContext();

  const [cityGeometry, setCityGeometry] = useState<Geometry | null>(null);
  const [cityName, setCityName] = useState<string>("");
  const [cityFeature, setCityFeature] = useState<GeoJsonObject | null>(null);
  const [isCityActive, setIsCityActive] = useState(false);

  // Clears the município highlight/selection left over from a previous selection
  // when the user selects a marking/drawing/imported layer instead.
  const handleCitySelectionCleared = useCallback(() => {
    setCityName("");
    setCityFeature(null);
  }, []);

  const [systemMode, setSystemMode] = useState<SystemMode>("sin");
  const selectedArea = useSelectedArea(systemMode, handleCitySelectionCleared);

  // Picking a projection here only drives the área propícia calculation (Dados
  // Técnicos) — it no longer adds the full raster layer to the map. That raster
  // is heavy (whole-state image) and the calculation itself is now shown as a
  // vector shape scoped to the selected area (see projectionPropitiousGeojson).
  // To see the full projection raster, enable it explicitly via Camadas → Projeções.
  const handleProjectionChange = useCallback(
    (layerName: string | null) => {
      selectedArea.setSelectedProjectionLayer(layerName);
    },
    [selectedArea.setSelectedProjectionLayer]
  );
  const { setShowLegend } = selectedArea;

  const areaDrawingOptions = useMemo(
    () =>
      drawings
        .filter((d) => d.type === MAP_TOOLS.MEASURE_AREA)
        .map((d) => ({ id: d.id, name: d.name })),
    [drawings]
  );

  const [exportingAreaId, setExportingAreaId] = useState<string | null>(null);

  // Exports the drawn area's active vector layers, plus the área propícia of
  // whichever projection family (solar/eólico) is currently selected in the
  // Estatísticas panel, as a .zip of .kml files scoped to that area. Raster
  // layers (GHI, vento, MDT) are out of scope — they're WMS tiles with no
  // coverage endpoint to crop server-side.
  const handleExportArea = useCallback(
    async (drawingId: string) => {
      const drawing = drawings.find((d) => d.id === drawingId);
      if (!drawing || drawing.type !== MAP_TOOLS.MEASURE_AREA) return;

      // @ts-ignore
      const geometry = drawing.layer.toGeoJSON().geometry;
      if (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")
        return;

      const areaM2 = computeAreaKm2(geometry) * 1_000_000;
      if (!isAreaExportable(areaM2)) {
        toast.error(
          `A exportação está disponível apenas para áreas de até ${AREA_EXPORT_MAX_AREA_HA} hectares.`
        );
        return;
      }

      setExportingAreaId(drawingId);
      try {
        const activeLayers = mockLayers.filter(
          (l) =>
            l.type === "feature" &&
            (l.permanent ||
              layers.includes(l.title) ||
              // Also export when a partial voltage filter is active (layer not in `layers` array)
              (l.voltageOptions &&
                (layerCqlFilters[l.layerName]?.length ?? 0) > 0))
        );

        const isEolico = EOLICO_PROJECTION_LAYER_OPTIONS.some(
          (p) => p.layerName === selectedArea.selectedProjectionLayer
        );
        // Export only the projection the user actually picked in "Área Propícia por:";
        // with no selection there is nothing to export (and no family to assume).
        const selectedProjection = [
          ...PROJECTION_LAYER_OPTIONS,
          ...EOLICO_PROJECTION_LAYER_OPTIONS,
        ].find((p) => p.layerName === selectedArea.selectedProjectionLayer);
        const projectionOptions = selectedProjection ? [selectedProjection] : [];
        const hubHeightM = isEolico
          ? parseInt(selectedArea.windHeight.replace(/\D/g, ""), 10) ||
            undefined
          : undefined;

        const { succeeded, failed } = await exportSelectedArea({
          geometry,
          areaName: drawing.name,
          activeLayers,
          layerCqlFilters,
          projectionOptions,
          hubHeightM,
        });

        if (succeeded.length === 0) {
          toast.error(
            "Não foi possível exportar nenhuma camada para esta área."
          );
        } else if (failed.length > 0) {
          toast.warning(
            `Pacote exportado com ${succeeded.length} camada(s). ${failed.length} não puderam ser incluídas (sem dados na área ou erro de rede).`
          );
        } else {
          toast.success("Exportação concluída.");
        }
      } catch (error) {
        console.error("Erro ao exportar a área:", error);
        toast.error("Erro ao exportar a área.");
      } finally {
        setExportingAreaId(null);
      }
    },
    [
      drawings,
      layers,
      layerCqlFilters,
      selectedArea.selectedProjectionLayer,
      selectedArea.windHeight,
    ]
  );

  const [showModal, setShowModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [statisticsPanelTab, setStatisticsPanelTab] =
    useState<StatisticsPanelTab>("statistics");
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [mapHoveredDrawingId, setMapHoveredDrawingId] = useState<string | null>(
    null
  );
  // Restrição apontada no memorial de cálculo: o painel avisa qual linha está
  // sob o mouse e o mapa desenha a parcela que cabe a ela.
  const [hoveredRestriction, setHoveredRestriction] =
    useState<RestrictionEntry | null>(null);

  const showErrorToast = (message: string) => {
    toast.error(message);
  };

  const isSolarActive = layers.some((layer) => {
    const l = layer.toUpperCase();
    return l.includes("GHI") || l.includes("TODOS");
  });
  const isGhiLayerActive = layers.some((l) => l.toUpperCase().includes("GHI"));

  const getCoordinatesFromCity = async (name: string) => {
    const coordinatesResp = await geoServer.get("wfs", {
      params: {
        ...geoServerGetCityCoordinatesParams,
        cql_filter: `nm_mun='${name}'`,
      },
    });

    const feature = coordinatesResp.data.features[0];
    const coords0 = feature.geometry.coordinates[0] as
      | number[][]
      | number[][][];
    let highlightCoords: number[][][];
    if (feature.geometry.type === "MultiPolygon") {
      highlightCoords = coords0 as number[][][];
    } else {
      highlightCoords = [coords0 as number[][]];
    }
    setCityName(name);
    setCityGeometry({
      coordinates: feature.geometry.coordinates[0],
      type: "Polygon",
    });
    setCityFeature({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: highlightCoords },
      properties: {},
    } as GeoJsonObject);
  };

  const getCityByCoordinate = async (lat: number, lng: number) => {
    try {
      const cityResp = await ApiReverseGeocoding({
        lat,
        lon: lng,
        table: "municipios_pe",
      });

      if (!cityResp?.name || cityResp.name === selectedArea.selectedCityName) {
        setCityName("");
        setCityFeature(null);
        selectedArea.handleCityDeselect();
        return;
      }

      selectedArea.setSelectedCityName(cityResp.name);
      getCoordinatesFromCity(cityResp.name);
    } catch (error) {
      console.error("Erro ao buscar coordenadas do município:", error);
      if (selectedArea.selectedCityName) {
        setCityName("");
        setCityFeature(null);
        selectedArea.handleCityDeselect();
      }
    }
  };

  const handleMapClick = async (lat: number, lng: number) => {
    if (activeFunction) return;

    const { lat: nLat, lng: nLng } = normalizeLatLng(lat, lng);
    if (!isInsidePernambuco(nLat, nLng)) {
      if (isCityActive && cityName) {
        setCityName("");
        setCityFeature(null);
        selectedArea.handleCityDeselect();
      }
      return;
    }
    if (isCityActive) {
      selectedArea.handleDrawingSelect(null);
      getCityByCoordinate(nLat, nLng);
    }

    if (!isSolarActive) return;

    await selectedArea.fetchSolarData(selectedArea.currentGeometry);
  };

  const handleToggle = (section: PanelSection) => {
    const togglers: Record<PanelSection, () => void> = {
      modal: () => {
        const next = !showModal;
        if (next) {
          setSelectedTab(0);
          activeFunctionHandler(null);
          setShowToolsMenu(false);
          setShowReportModal(false);
        }
        setShowModal(next);
      },
      tools: () => {
        const next = !showToolsMenu;
        if (next) {
          setShowModal(false);
          setShowReportModal(false);
        } else {
          activeFunctionHandler(null);
        }
        setShowToolsMenu(next);
      },
      report: () => {
        const next = !showReportModal;
        if (next) {
          setShowToolsMenu(false);
          setShowModal(false);
        }
        setShowReportModal(next);
      },
      legend: () => {
        setStatisticsPanelTab("statistics");
        selectedArea.setShowLegend((prev) => !prev);
      },
    };
    togglers[section]();
  };

  const openLayersPanel = useCallback(() => {
    setSelectedTab(0);
    activeFunctionHandler(null);
    setShowToolsMenu(false);
    setShowReportModal(false);
    setShowModal(true);
  }, [activeFunctionHandler]);

  const openProjectionsPanel = useCallback(() => {
    setSelectedTab(1);
    activeFunctionHandler(null);
    setShowToolsMenu(false);
    setShowReportModal(false);
    setShowModal(true);
  }, [activeFunctionHandler]);

  const openBaseMapPanel = useCallback(() => {
    setSelectedTab(2);
    activeFunctionHandler(null);
    setShowToolsMenu(false);
    setShowReportModal(false);
    setShowModal(true);
  }, [activeFunctionHandler]);

  const openStatisticsPanel = useCallback(() => {
    setStatisticsPanelTab("statistics");
    setShowLegend(true);
  }, [setShowLegend]);

  const openLegendsPanel = useCallback(() => {
    setStatisticsPanelTab("legends");
    setShowLegend(true);
  }, [setShowLegend]);

  const openReportModal = useCallback(() => {
    activeFunctionHandler(null);
    setShowModal(false);
    setShowToolsMenu(false);
    setShowLegend(false);
    setShowReportModal(true);
  }, [activeFunctionHandler, setShowLegend]);

  const openToolsMenu = useCallback(() => {
    activeFunctionHandler(null);
    setShowModal(false);
    setShowReportModal(false);
    setShowLegend(false);
    setShowToolsMenu(true);
  }, [activeFunctionHandler, setShowLegend]);

  const closeToolsMenu = useCallback(() => {
    activeFunctionHandler(null);
    setShowToolsMenu(false);
  }, [activeFunctionHandler]);

  useEffect(() => {
    if (setOnSocioambientalLayerAdded) {
      setOnSocioambientalLayerAdded(() => {
        toast("Informação", {
          description: (
            <Alert variant="info">
              <a
                href="https://plataformaecope.cprh.pe.gov.br/dashboard"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Clique aqui
              </a>{" "}
              para obter mais informações sobre licenciamento ambiental
              (Plataforma Ecológica-Econômica CPRH)
            </Alert>
          ),
        });
      });
    }

    if (setOnAplLayerAdded) {
      setOnAplLayerAdded(() => {
        toast("Informação", {
          description: (
            <Alert variant="info">
              <a
                href="https://www.adepe.pe.gov.br/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Clique aqui
              </a>{" "}
              para obter mais informações sobre APL (Arranjos Produtivos Locais)
            </Alert>
          ),
        });
      });
    }

    if (setOnSgbLayerAdded) {
      setOnSgbLayerAdded(() => {
        toast("Informação", {
          description: (
            <Alert variant="info">
              Para mais informações sobre os dados geológicos,{" "}
              <a
                href="https://www.sgb.gov.br/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                clique aqui
              </a>{" "}
              para o portal do SGB e{" "}
              <a
                href="https://geosgb.sgb.gov.br/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                aqui
              </a>{" "}
              para o GeoSGB (mapa interativo).
            </Alert>
          ),
        });
      });
    }

    if (setOnDgcLayerAdded) {
      setOnDgcLayerAdded(() => {
        openLegendsPanel();
      });
    }

    if (setOnDistributionSubstationLayerAdded) {
      setOnDistributionSubstationLayerAdded(() => {
        toast("Informação", {
          description: (
            <Alert variant="info">
              Para <strong>micro e minigeração</strong> (até 3 MW em solar),{" "}
              <a
                href={NEOENERGIA_GERACAO_DISTRIBUIDA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                clique aqui
              </a>{" "}
              e veja como solicitar{" "}
              <strong>acesso à Geração Distribuída</strong>. Para{" "}
              <strong>usinas maiores</strong>, o acesso segue as normas de{" "}
              <strong>centrais geradoras</strong>:{" "}
              <a
                href={NEOENERGIA_CENTRAIS_GERADORAS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                clique aqui
              </a>{" "}
              para ver as normas técnicas da Neoenergia (ex.: DIS-NOR-067, para
              69/88/138 kV). Para uma{" "}
              <strong>nova ligação ou aumento de demanda</strong> de consumo,{" "}
              <a
                href={NEOENERGIA_VIABILIDADE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                clique aqui
              </a>{" "}
              e abra <strong>Disponibilidade de Energia (Viabilidade)</strong>{" "}
              na página de serviços.
            </Alert>
          ),
        });
      });
    }
  }, [
    setOnSocioambientalLayerAdded,
    setOnAplLayerAdded,
    setOnSgbLayerAdded,
    setOnDgcLayerAdded,
    setOnDistributionSubstationLayerAdded,
    openLegendsPanel,
  ]);

  useEffect(() => {
    if (layers.some((layer) => layer.toLowerCase().includes("municípios"))) {
      setIsCityActive(true);
      if (cityName) {
        selectedArea.handleCityAnalysis(cityGeometry, cityName);
      }
      return;
    }
    setIsCityActive(false);
    setCityFeature(null);
    // Only clear the city-derived stats if a city is actually the active
    // selection — otherwise toggling an unrelated map layer would wipe out
    // an active drawing/area selection's data.
    if (selectedArea.selectedCityName) {
      selectedArea.handleCityDeselect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityGeometry, layers]);

  return (
    <HomeContainer style={{ position: "relative" }}>
      <div id="main-map" className="leaflet-container">
        <HomeJoyride
          openProjectionsPanel={openProjectionsPanel}
          openBaseMapPanel={openBaseMapPanel}
          openLayersPanel={openLayersPanel}
          openStatisticsPanel={openStatisticsPanel}
          openLegendsPanel={openLegendsPanel}
          openReportModal={openReportModal}
          openToolsMenu={openToolsMenu}
          closeToolsMenu={closeToolsMenu}
        />
        <Navbar />
        <MapComponent>
          <FerroviaSvgDefs />
          <MapClickHandler onMapClick={handleMapClick} />
          <GeocodingSearchBar table="municipios_pe" limit={5} />
          <MapActionBar
            showModal={showModal}
            handleToggleModal={() => handleToggle("modal")}
            showReportModal={showReportModal}
            handleToggleReportModal={() => handleToggle("report")}
            showToolsMenu={showToolsMenu}
            handleToggleToolsMenu={() => handleToggle("tools")}
            showLegend={selectedArea.showLegend}
            onLegendToggle={() => handleToggle("legend")}
          >
            <RasterGradientLegend />
          </MapActionBar>

          <FeatureGroup ref={featureGroupRef}>
            <ToolsHandler
              featureGroupRef={featureGroupRef}
              onInvalidLocation={showErrorToast}
              onMapDrawingClick={selectedArea.handleDrawingSelect}
              onMapDrawingHover={setMapHoveredDrawingId}
            />
          </FeatureGroup>
          <RenderImportedLayers />
          <RenderGeoServerLayers />
          {isCityActive && cityFeature && (
            <GeoJSON
              key={cityName}
              data={cityFeature}
              style={{
                color: "#f97316",
                weight: 2,
                fillColor: "#f97316",
                fillOpacity: 0.15,
              }}
              interactive={false}
            />
          )}
          {selectedArea.projectionPropitiousGeojson && (
            <GeoJSON
              key={`projection-propitious-${selectedArea.selectedProjectionLayer}-${selectedArea.selectedAreaDrawingId}`}
              data={selectedArea.projectionPropitiousGeojson}
              style={{
                color: "#16a34a",
                weight: 1.5,
                fillColor: "#22c55e",
                fillOpacity: 0.45,
              }}
              interactive={false}
            />
          )}
          {selectedArea.showLegend && hoveredRestriction?.geometry && (
            <GeoJSON
              // O <GeoJSON> do react-leaflet ignora trocas de `data`; sem o id da
              // restrição na key, apontar outra linha manteria a forma anterior.
              key={`restriction-highlight-${hoveredRestriction.id}-${selectedArea.selectedAreaDrawingId}`}
              data={hoveredRestriction.geometry}
              style={{
                color: "#b45309",
                weight: 1.5,
                fillColor: "#f59e0b",
                fillOpacity: 0.5,
              }}
              interactive={false}
            />
          )}
          <ProximityHoverManager tolerance={15} />
          <DimensionControl />
          <RasterRangeControl />
        </MapComponent>
      </div>

      <ConjuntoDGCModal />

      <ToolsMenu
        isOpen={showToolsMenu}
        onClose={closeToolsMenu}
        onAreaConfirmed={(geometry, drawingId) => {
          setCityFeature(null);
          selectedArea.handleAreaAnalysis(geometry, drawingId);
        }}
        onDrawingSelect={(id) => {
          if (id !== null) setCityFeature(null);
          selectedArea.handleDrawingSelect(id);
        }}
        onImportedLayerSelect={async (id) => {
          setCityFeature(null);
          await selectedArea.handleImportedLayerSelect(id);
        }}
        onReportDrawing={(drawingId) => {
          setCityFeature(null);
          selectedArea.handleDrawingSelect(drawingId);
          setShowToolsMenu(false);
          setShowReportModal(true);
        }}
        onExportArea={handleExportArea}
        exportingDrawingId={exportingAreaId}
        selectedDrawingId={selectedArea.selectedDrawingId}
        hoveredDrawingId={mapHoveredDrawingId}
      />

      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        showModal={showModal}
        selectedDrawingId={selectedArea.selectedDrawingId}
        selectedCityName={selectedArea.selectedCityName}
        selectedCityGeometry={cityGeometry || null}
        systemMode={systemMode}
        onSelectAreaMarking={selectedArea.handleDrawingSelect}
        onSelectCityMarking={() => {
          if (cityGeometry && selectedArea.selectedCityName) {
            selectedArea.handleCityAnalysis(
              cityGeometry,
              selectedArea.selectedCityName
            );
          }
        }}
        onDeselectMarking={() => selectedArea.handleDrawingSelect(null)}
      />

      <InstructionCard />

      {showModal && (
        <TabComponent
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          onClose={() => setShowModal(false)}
        />
      )}

      <div
        style={
          selectedArea.showLegend
            ? undefined
            : { visibility: "hidden", pointerEvents: "none" }
        }
      >
        <StatisticsPanel
          ref={statisticsPanelRef}
          onClose={() => selectedArea.setShowLegend(false)}
          activeTab={statisticsPanelTab}
          onActiveTabChange={setStatisticsPanelTab}
          latitude={null}
          longitude={null}
          address={null}
          globalIrradiation={null}
          difuseIrradiation={null}
          directIrradiation={null}
          monthlyIrradiation={
            selectedArea.monthlyData.length > 0
              ? selectedArea.monthlyData
              : Array(12).fill(0)
          }
          annualMean={selectedArea.annualMean}
          distributionData={selectedArea.distributionData}
          areaStats={selectedArea.areaStats}
          areaPotential={selectedArea.areaPotential}
          areaWindPotential={selectedArea.areaWindPotential}
          distanceData={selectedArea.distanceData}
          isLoadingDistance={selectedArea.isLoadingDistance}
          selectedDrawing={
            selectedArea.selectedAreaDrawingId
              ? drawings.find(
                  (d) => d.id === selectedArea.selectedAreaDrawingId
                ) ?? null
              : null
          }
          areaDrawings={areaDrawingOptions}
          onSelectAreaDrawing={selectedArea.handleDrawingSelect}
          selectedCityName={selectedArea.selectedCityName}
          hasSelection={!!selectedArea.currentGeometry}
          selectionAreaKm2={selectedArea.selectionAreaKm2}
          windHeight={selectedArea.windHeight}
          onWindHeightChange={selectedArea.setWindHeight}
          windScenario={selectedArea.windScenario}
          onWindScenarioChange={selectedArea.setWindScenario}
          windSeriesData={selectedArea.windSeriesData}
          windDensityData={selectedArea.windDensityData}
          windDistributionData={selectedArea.windDistributionData}
          windPotentialData={selectedArea.windPotentialData}
          windCumulativeData={selectedArea.windCumulativeData}
          windRoseData={selectedArea.windRoseData}
          isStaticSolarLayer={isGhiLayerActive}
          windDensityDistributionData={selectedArea.windDensityDistributionData}
          windDensityAreaStats={selectedArea.windDensityAreaStats}
          systemMode={systemMode}
          onSystemModeChange={setSystemMode}
          generationPowerSummary={selectedArea.generationPowerSummary}
          selectedProjectionLayer={selectedArea.selectedProjectionLayer}
          onProjectionChange={handleProjectionChange}
          projectionAreaStats={selectedArea.projectionAreaStats}
          selectedGeometry={selectedArea.currentGeometry}
          isLoadingProjection={selectedArea.isLoadingProjection}
          isLoadingWindPotential={selectedArea.isLoadingWindPotential}
          onCalculatePotential={selectedArea.calculatePotential}
          isLoadingPotential={selectedArea.isLoadingPotential}
          onCalculateDistance={selectedArea.calculateDistance}
          onHoverRestriction={setHoveredRestriction}
        />
      </div>

      <Footer />
    </HomeContainer>
  );
}

export default Home;
